import React from 'react';
import { Box, Checkbox, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SimpleAutoComplete from '../auto-complete/autocomplete';
import SimpleAutoCompleteMultipleRequired from '../auto-complete/autocomplete-multiple-required';
import SimpleCheckBox from '../checkbox/check';
import SimpleDatePickerRequired from '../date-pickers/inline-required';
import RegexInput from '../regex-input/regex-input';
import { FIELD_TYPE, parseDateValue, toIsoDate } from './helpers';

/**
 * Motor de renderização de UM campo personalizado, por tipo (factory/strategy).
 *
 * É um componente CONTROLADO e PURO: recebe `value` no formato único
 * `{ value, optionId, multipleIds }` e devolve o novo valor no MESMO formato via `onChange`.
 * Não conhece chamado, API nem estado global — quem orquestra é o componente pai.
 *
 * Cobre os 10 tipos do enum OrganizationFieldType, reutilizando os mesmos componentes
 * que as telas legadas já usam (para manter a identidade visual).
 */
function OrganizationFieldInput(props) {
  const { field, value, onChange, disabled, required, likertCardLayout } = props;

  const type = field.organizationfieldtype;
  const options = field.organizationfieldoptionsvalues || [];
  const inputId = `ofield-${field.id}`;
  const maxLength = field.maxlength ? field.maxlength : 250;

  const current = value || { value: '', optionId: null, multipleIds: [] };
  const emit = (patch) => onChange({ ...current, ...patch });

  switch (type) {
    // ---- Rádio: valor textual = nome da opção; guarda também o id da opção ----
    case FIELD_TYPE.RADIO:
      return (
        <FormControl required={required} disabled={disabled} component="fieldset" sx={{ minWidth: 0 }}>
          <FormLabel id={`radio-label-${field.id}`}>{field.name}</FormLabel>
          <RadioGroup
            name={`radio-group-${field.id}`}
            value={current.optionId != null ? String(current.optionId) : ''}
            onChange={(event) => {
              const optionId = Number(event.target.value);
              const option = options.find((o) => Number(o.id) === optionId);
              emit({ value: option ? option.name : '', optionId });
            }}>
            {options.map((option) => (
              <FormControlLabel
                key={`radio-${option.id}`}
                value={String(option.id)}
                control={<Radio required={required} />}
                label={option.name}
                disabled={disabled} />
            ))}
          </RadioGroup>
        </FormControl>
      );

    case FIELD_TYPE.LIKERT: {
      const statements = field.likertstatements || [];
      const answers = current.likertAnswers || {};
      const isCheckbox = Number(field.likertselectionmode) === 2;
      const selectedOf = (st) => (Array.isArray(answers[st.id]) ? answers[st.id] : []);
      const columns =
        statements[0] && Array.isArray(statements[0].options) ? statements[0].options : [];

      const setSingle = (st, optId) =>
        emit({ likertAnswers: { ...answers, [st.id]: [Number(optId)] } });
      const toggleMulti = (st, optId) => {
        const selected = selectedOf(st).map(Number);
        const next = selected.includes(Number(optId))
          ? selected.filter((x) => x !== Number(optId))
          : [...selected, Number(optId)];
        emit({ likertAnswers: { ...answers, [st.id]: next } });
      };

      if (likertCardLayout) {
        return (
          <FormControl
            disabled={disabled}
            component="fieldset"
            sx={{ width: '100%', minWidth: 0 }}>
            <FormLabel
              component="legend"
              id={`likert-label-${field.id}`}
              sx={{
                fontSize: 20,
                fontWeight: 400,
                lineHeight: 1.3,
                color: 'text.primary',
                mb: 1.5,
                '&.Mui-focused': { color: 'text.primary' },
              }}>
              {field.name}
            </FormLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
              {statements.map((st) => {
                const selected = selectedOf(st).map(Number);
                return (
                  <Box
                    key={`likert-st-${st.id}`}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px',
                      p: 1.5,
                      minWidth: 0,
                    }}>
                    <Box
                      sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: 'text.primary',
                        mb: 1,
                        wordBreak: 'break-word',
                      }}>
                      {st.name}
                      {required ? ' *' : ''}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {columns.map((col, ci) => {
                        const opt = (st.options || [])[ci] || col;
                        const optId = Number(opt.id);
                        const checked = selected.includes(optId);
                        return (
                          <FormControlLabel
                            key={`likert-opt-${st.id}-${ci}`}
                            disabled={disabled}
                            labelPlacement="start"
                            sx={{
                              ml: 0,
                              width: '100%',
                              justifyContent: 'space-between',
                              py: 0.5,
                              borderBottom: ci < columns.length - 1 ? '1px dashed' : 'none',
                              borderColor: 'divider',
                            }}
                            label={
                              <Box sx={{ fontSize: 13, color: 'text.secondary', wordBreak: 'break-word' }}>
                                {opt.name}
                              </Box>
                            }
                            control={
                              isCheckbox ? (
                                <Checkbox
                                  color="primary"
                                  checked={checked}
                                  disabled={disabled}
                                  onChange={() => toggleMulti(st, optId)} />
                              ) : (
                                <Radio
                                  color="primary"
                                  name={`likert-row-${field.id}-${st.id}`}
                                  value={String(optId)}
                                  checked={checked}
                                  disabled={disabled}
                                  required={required && selected.length === 0}
                                  onChange={() => setSingle(st, optId)} />
                              )
                            } />
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </FormControl>
        );
      }

      const headCellSx = {
        px: 1.5,
        py: 1.5,
        fontSize: 13,
        fontWeight: 600,
        color: 'text.secondary',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        textAlign: 'center',
        wordBreak: 'break-word',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        borderBottom: '1px solid',
        borderColor: 'divider',
      };
      const bodyCellSx = {
        px: 1.5,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
      const separator = { borderTop: '1px solid', borderColor: 'divider' };

      return (
        <FormControl
          disabled={disabled}
          component="fieldset"
          sx={{ width: '100%' }}>
          <FormLabel
            component="legend"
            id={`likert-label-${field.id}`}
            sx={{
              fontSize: 20,
              fontWeight: 700,
              lineHeight: 1.3,
              color: 'text.primary',
              mb: 1.5,
              '&.Mui-focused': { color: 'text.primary' },
            }}>
            {field.name}
          </FormLabel>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px',
              overflow: 'hidden',
            }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Box
                sx={{
                  minWidth: 220 + columns.length * 110,
                  display: 'grid',
                  gridTemplateColumns: `minmax(180px, 1.4fr) repeat(${columns.length}, minmax(90px, 1fr))`,
                }}>
                <Box sx={{ ...headCellSx, justifyContent: 'flex-start', textAlign: 'left' }} />
                {columns.map((col, ci) => (
                  <Box key={`likert-col-${field.id}-${ci}`} sx={headCellSx}>
                    {col.name}
                  </Box>
                ))}
                {statements.map((st, ri) => {
                  const selected = selectedOf(st).map(Number);
                  const rowSep = ri > 0 ? separator : null;
                  return (
                    <React.Fragment key={`likert-st-${st.id}`}>
                      <Box
                        sx={{
                          ...bodyCellSx,
                          ...rowSep,
                          justifyContent: 'flex-start',
                          fontSize: 14,
                          fontWeight: 500,
                          wordBreak: 'break-word',
                        }}>
                        {st.name}
                        {required ? ' *' : ''}
                      </Box>
                      {columns.map((col, ci) => {
                        const opt = (st.options || [])[ci] || col;
                        const optId = Number(opt.id);
                        const checked = selected.includes(optId);
                        return (
                          <Box
                            key={`likert-cell-${st.id}-${ci}`}
                            sx={{ ...bodyCellSx, ...rowSep }}>
                            {isCheckbox ? (
                              <Checkbox
                                color="primary"
                                checked={checked}
                                disabled={disabled}
                                onChange={() => toggleMulti(st, optId)} />
                            ) : (
                              <Radio
                                color="primary"
                                name={`likert-row-${field.id}-${st.id}`}
                                value={String(optId)}
                                checked={checked}
                                disabled={disabled}
                                required={required && selected.length === 0}
                                onChange={() => setSingle(st, optId)} />
                            )}
                          </Box>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </FormControl>
      );
    }

    // ---- Seleção (autocomplete simples) ----
    case FIELD_TYPE.SELECT: {
      const selected = options.find((o) => Number(o.id) === Number(current.optionId)) || { id: 0, name: '' };
      return (
        <Tooltip title={field.name} placement="top-start" arrow>
          <div>
            <SimpleAutoComplete
              label={field.name}
              options={options}
              stateName={inputId}
              selected={selected}
              required={required}
              disabled={disabled}
              changeSelect={(stateName, option) =>
                emit({ value: option ? option.name : '', optionId: option ? option.id : null })
              } />
          </div>
        </Tooltip>
      );
    }

    // ---- Seleção Múltipla: N opções -> N linhas no backend ----
    case FIELD_TYPE.SELECT_MULTIPLE: {
      const selectedValues = options.filter((o) => (current.multipleIds || []).includes(Number(o.id)));
      return (
        <Tooltip title={field.name} placement="top-start" arrow>
          <div>
            <SimpleAutoCompleteMultipleRequired
              label={field.name}
              options={options}
              stateName={inputId}
              selected={selectedValues}
              required={required}
              disabled={disabled}
              changeSelect={(stateName, selection) => {
                const list = selection || [];
                emit({
                  value: list.map((s) => s.name).join(','),
                  optionId: null,
                  multipleIds: list.map((s) => Number(s.id)),
                });
              }} />
          </div>
        </Tooltip>
      );
    }

    // ---- Checkbox: persistido como string 'true'/'false' ----
    case FIELD_TYPE.CHECKBOX:
      return (
        <SimpleCheckBox
          label={field.name}
          name={inputId}
          stateName={inputId}
          disabled={disabled}
          selected={String(current.value) === 'true'}
          changeSelect={(stateName, checked) => emit({ value: String(checked === true) })} />
      );

    // ---- Número inteiro ----
    case FIELD_TYPE.NUMBER:
      return (
        <Tooltip title={field.name} placement="top-start" arrow>
          <TextField
            id={inputId}
            name={inputId}
            label={field.name}
            variant="outlined"
            fullWidth
            required={required}
            disabled={disabled}
            value={current.value || ''}
            InputLabelProps={{ shrink: true }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength }}
            onChange={(e) => emit({ value: e.target.value.replace(/\D/g, '').slice(0, maxLength) })} />
        </Tooltip>
      );

    // ---- Número decimal (aceita vírgula, normaliza pra ponto) ----
    case FIELD_TYPE.DECIMAL:
      return (
        <Tooltip title={field.name} placement="top-start" arrow>
          <TextField
            id={inputId}
            name={inputId}
            label={field.name}
            variant="outlined"
            fullWidth
            required={required}
            disabled={disabled}
            value={current.value || ''}
            InputLabelProps={{ shrink: true }}
            inputProps={{ inputMode: 'decimal', pattern: '^[0-9]*[.,]?[0-9]*$', maxLength }}
            onChange={(e) =>
              emit({ value: e.target.value.replace(',', '.').replace(/[^0-9.]/g, '').slice(0, maxLength) })
            } />
        </Tooltip>
      );

    // ---- Texto multilinha ----
    case FIELD_TYPE.TEXT_MULTIPLE_LINE:
      return (
        <Tooltip title={field.name} placement="top-start" arrow>
          <TextField
            id={inputId}
            name={inputId}
            label={field.name}
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            required={required}
            disabled={disabled}
            value={current.value || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => emit({ value: e.target.value })} />
        </Tooltip>
      );

    // ---- Data ----
    case FIELD_TYPE.DATE:
      return (
        <SimpleDatePickerRequired
          label={field.name}
          stateName={inputId}
          toolTipTitle={field.name}
          required={required}
          disabled={disabled}
          currentDate={parseDateValue(current.value)}
          changeDate={(stateName, date) => emit({ value: toIsoDate(date) })} />
      );

    // ---- Texto com Regex (a máscara/regex vem na 1ª "opção" do campo) ----
    case FIELD_TYPE.REGEX_TEXT:
      return (
        <RegexInput
          id={inputId}
          name={inputId}
          label={field.name}
          regex={options.length > 0 ? options[0].name : ''}
          required={required}
          disabled={disabled}
          defaultValue={current.value || ''}
          onChangeValue={(e) => emit({ value: e.target.value })} />
      );

    // ---- Texto (default) ----
    case FIELD_TYPE.TEXT:
    default:
      return (
        <Tooltip title={field.name} placement="top-start" arrow>
          <TextField
            id={inputId}
            name={inputId}
            label={field.name}
            variant="outlined"
            fullWidth
            required={required}
            disabled={disabled}
            value={current.value || ''}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength }}
            onChange={(e) => emit({ value: e.target.value })} />
        </Tooltip>
      );
  }
}

export default OrganizationFieldInput;
